import { useEffect, useRef, useState } from 'react';
import { useAuth } from '@/hooks/app/useAuth';
import { useProjects } from '@/hooks/project/useProjects';
import { useFileUpload } from '@/hooks/file/useFileUpload';
import { useConversations } from '@/hooks/conversation/useConversations';
import { useResponsiveLayout } from '@/hooks/ui/useResponsiveLayout';
import { useAppHandlers } from '@/hooks/app/useAppHandlers';
import { useAppInitialization } from '@/hooks/app/useAppInitialization';
import { useChatViewState } from '@/hooks/chat/useChatViewState';
import { useArtifactSidebarSync } from '@/hooks/ui/useArtifactSidebarSync';
import { useSidebarHandlers } from '@/hooks/sidebar/useSidebarHandlers';
import {
  useAuthStore,
  useProjectsStore,
  useConversationsStore,
  useUIStore,
} from '@/stores';
import { FilesProvider } from '@/contexts/FilesContext';
import { ConversationsProvider } from '@/contexts/ConversationsContext';
import { MessageStreamProvider } from '@/contexts/MessageStreamContext';
import { HashRouterProvider } from '@/contexts/HashRouterContext';
import Sidebar from '@/components/Sidebar';
import MainContent from '@/components/MainContent';
import Login from '@/components/Login';
import NewProjectModal from '@/components/NewProjectModal';
import { showToast } from '@/components/Toast';
import { Toaster } from '@/components/ui/toaster';
import SimplePDFViewer from '@/components/SimplePDFViewer';
import FileUploadModal from '@/components/FileUploadModal';
import VersionInfo from '@/components/VersionInfo';
import { MobileFullscreenExitButton } from '@/components/MobileHeader';
// import SkillTaskDashboard from '@/components/SkillTaskDashboard'; // moved to MainContent.tsx for split-screen rendering
import {
  Search,
  GalleryVerticalEnd,
  Share2,
  Grid2x2,
  Plus,
} from 'lucide-react';
import '@/types/global.d.ts';

function AppContent() {
  const stopRequestRef = useRef<(() => void) | null>(null);

  // Project members management state
  const [showMembersForProject, setShowMembersForProject] = useState<{
    projectId: string;
    projectName: string;
  } | null>(null);

  // Chat view state
  const {
    chatReadOnly,
    sharedConversationRecordId,
    setChatReadOnly,
    setSharedConversationRecordId,
  } = useChatViewState();

  // Zustand stores
  const { isLoggedIn, userInfo, loginUrl } = useAuthStore();
  const { currentProjectId } = useProjectsStore();
  const { chatPageSelectedConversationId, setChatPageSelectedConversationId } =
    useConversationsStore();
  const {
    currentView,
    isSidebarCollapsed,
    isMobileFullscreen,
    selectedNavItem,
    shareConversationId,
    showNewProjectModal,
    pdfViewer,
    setCurrentView,
    setIsSidebarCollapsed,
    setSelectedNavItem,
    setShareConversationId,
    setShowNewProjectModal,
    setPdfViewer,
  } = useUIStore();

  // Authentication hooks
  const { logout } = useAuth();

  // Projects hooks
  const { loadProjects, selectProject } = useProjects();

  // Conversations state for Sidebar
  const {
    conversations,
    isLoading: isLoadingConversations,
    loadConversations,
    createNewConversation,
    updateConversation,
    refreshConversations,
  } = useConversations(null);

  // File upload hook
  const {
    showUploadModal,
    uploadFiles,
    isUploadingFiles,
    autoCloseCountdown,
    uploadProjectFiles,
    closeUploadModal,
    removeUploadFile,
  } = useFileUpload();

  // Responsive layout hook
  const { isMobile } = useResponsiveLayout();

  // App handlers hook
  const {
    handleNewProject,
    handleCreateProject,
    handleProjectSelect,
    handleRenameProject,
    handleDeleteProject,
    handleStarProject,
    handleUpdateProject,
    handleBackToGeneralChats,
    handleBackToProjects,
    handleShowPDFList,
    onShowAgentList,
    onShowProjectsList,
    onShowSharedList,
    onShowGroupChats,
    handleBackToChat,
    handleProjectConversationSelect,
    handleSwitchToChat,
  } = useAppHandlers({
    loadProjects,
    refreshConversations,
    selectProject,
    // 🔥 修复：传递这两个 setter，用于从项目详情页选择 conversation 时清除 shared 状态
    setChatReadOnly,
    setSharedConversationRecordId,
  });

  // App initialization hook
  const { handleLoginSuccess: onInitLoginSuccess } = useAppInitialization({
    shareConversationId,
    setShareConversationId,
    loadProjects,
    loadConversations,
    refreshConversations,
  });

  // Artifact sidebar sync hook
  useArtifactSidebarSync();

  // Sidebar handlers hook
  const {
    handleConversationSelect,
    handleNewConversation,
    handleUpdateConversation,
    handleRenameConversation,
  } = useSidebarHandlers({
    createNewConversation,
    updateConversation,
    selectProject,
    setChatReadOnly,
    setSharedConversationRecordId,
  });

  // 🔥 移动端自动折叠侧边栏
  useEffect(() => {
    if (isMobile) {
      setIsSidebarCollapsed(true);
    }
  }, [isMobile, setIsSidebarCollapsed]);

  // Clear Members view when switching away from projects-list
  useEffect(() => {
    if (currentView !== 'projects-list') {
      setShowMembersForProject(null);
    }
  }, [currentView]);

  // Handle login success
  const handleLoginSuccess = async (token: string) => {
    await onInitLoginSuccess(token);
  };

  const handleLoginError = (error: string) => {
    console.error('Login error:', error);
  };

  const handleLogout = async () => {
    await logout();
    setCurrentView('login');
  };

  // Login view
  if (currentView === 'login') {
    return (
      <Login
        loginUrl={loginUrl}
        onLoginSuccess={handleLoginSuccess}
        onLoginError={handleLoginError}
      />
    );
  }

  return (
    <FilesProvider>
      <div className="flex h-full bg-claude-bg overflow-hidden">
        {/* Version Info */}
        <div className="fixed top-2 right-4 z-50 rounded-lg bg-transparent lg:block hidden">
          <VersionInfo />
        </div>
        {/* Mobile sidebar backdrop */}
        {isMobile && !isSidebarCollapsed && (
          <button
            type="button"
            aria-label="Close sidebar"
            className="fixed inset-0 z-[70] bg-black/40"
            onClick={() => setIsSidebarCollapsed(true)}
          />
        )}

        {/* Sidebar */}
        <div
          className={`flex-shrink-0 h-full ${
            isMobile && isSidebarCollapsed
              ? 'hidden'
              : isMobile && !isSidebarCollapsed
                ? 'fixed inset-y-0 left-0 z-[80]'
                : 'relative'
          } ${isUploadingFiles ? 'pointer-events-none opacity-50' : ''}`}
        >
          <Sidebar
            conversations={conversations}
            projectConversations={[]}
            currentConversationId={chatPageSelectedConversationId}
            isCollapsed={isSidebarCollapsed}
            isLoadingGeneralConversations={isLoadingConversations}
            shareConversationId={shareConversationId}
            isLoggedIn={isLoggedIn}
            selectedNavItem={selectedNavItem}
            userInfo={userInfo || undefined}
            currentProjectId={currentProjectId}
            onConversationSelect={id => {
              handleConversationSelect(id);
              if (isMobile) setIsSidebarCollapsed(true);
            }}
            onNewConversation={handleNewConversation}
            onUpdateConversation={handleUpdateConversation}
            onRenameConversation={handleRenameConversation}
            onToggleCollapse={() => setIsSidebarCollapsed(!isSidebarCollapsed)}
            onShowAgentList={onShowAgentList}
            onShowSharedList={onShowSharedList}
            onShowGroupChats={onShowGroupChats}
            onBackToChat={handleBackToChat}
            onSelectedNavItem={setSelectedNavItem}
            onLogin={() => setCurrentView('login')}
            onLogout={handleLogout}
            onNewProject={handleNewProject}
            onProjectSelect={handleProjectSelect}
            onBackToGeneralChats={handleBackToGeneralChats}
            onRenameProject={handleRenameProject}
            onShowProjectsList={onShowProjectsList}
          />
        </div>

        {/* Main Content */}
        <MainContent
          chatReadOnly={chatReadOnly}
          sharedConversationRecordId={sharedConversationRecordId}
          chatPageSelectedConversationId={chatPageSelectedConversationId}
          shareConversationId={shareConversationId}
          stopRequestRef={stopRequestRef}
          showMembersForProject={showMembersForProject}
          isUploadingFiles={isUploadingFiles}
          onSetChatReadOnly={setChatReadOnly}
          onSetSharedConversationRecordId={setSharedConversationRecordId}
          onSetChatPageSelectedConversationId={
            setChatPageSelectedConversationId
          }
          onSetShowMembersForProject={setShowMembersForProject}
          onShowSharedList={onShowSharedList}
          onHandleNewProject={handleNewProject}
          onHandleStarProject={handleStarProject}
          onHandleDeleteProject={(projectId, role) =>
            handleDeleteProject(projectId, role)
          }
          onHandleShowPDFList={handleShowPDFList}
          onHandleProjectConversationSelect={handleProjectConversationSelect}
          onHandleSwitchToChat={handleSwitchToChat}
          onHandleBackToProjects={handleBackToProjects}
          onHandleRenameProject={handleRenameProject}
          onHandleUpdateProject={handleUpdateProject}
          onHandleProjectSelect={handleProjectSelect}
          onRefreshConversations={refreshConversations}
          uploadProjectFiles={uploadProjectFiles}
        />

        {/* PDF Viewer Modal */}
        {pdfViewer && (
          <SimplePDFViewer
            url={pdfViewer.url}
            title={pdfViewer.title}
            onClose={() => setPdfViewer(null)}
          />
        )}

        {/* New Project Modal */}
        <NewProjectModal
          isOpen={showNewProjectModal}
          onClose={() => setShowNewProjectModal(false)}
          onCreateProject={handleCreateProject}
          onShowToast={(message, type) => showToast(message, type)}
        />

        {/* File Upload Progress Modal */}
        <FileUploadModal
          isOpen={showUploadModal}
          files={uploadFiles}
          onClose={closeUploadModal}
          onRemoveFile={removeUploadFile}
          autoCloseCountdown={autoCloseCountdown}
        />

        {/* Global Upload Loading Overlay */}
        {isUploadingFiles && !showUploadModal && (
          <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
            <div className="bg-white rounded-lg p-6 flex flex-col items-center gap-4 shadow-xl">
              <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-claude-orange"></div>
              <div className="text-center">
                <p className="text-lg font-medium text-gray-800">
                  Uploading files...
                </p>
              </div>
            </div>
          </div>
        )}

        {/* Mobile Fullscreen Exit Button */}
        <MobileFullscreenExitButton />

        {/* 🔥 Skill Task Dashboard moved to MainContent.tsx for split-screen rendering */}

        {/* Mobile/Tablet Bottom Navigation Bar */}
        <nav
          className={`lg:hidden fixed bottom-0 left-0 right-0 z-50 bg-white border-t border-gray-200 flex items-center justify-around px-2 py-1.5 ${!isSidebarCollapsed || isMobileFullscreen ? 'hidden' : ''}`}
        >
          <button
            onClick={() => {
              setSelectedNavItem('chats');
              handleBackToChat();
              if (currentProjectId) handleBackToGeneralChats();
            }}
            className={`flex flex-col items-center gap-0.5 px-3 py-1 rounded-lg transition-colors ${
              selectedNavItem === 'chats' || !selectedNavItem
                ? 'text-orange-500'
                : 'text-gray-400'
            }`}
          >
            <Search size={20} />
            <span className="text-[10px] font-medium">Chats</span>
          </button>
          <button
            onClick={() => {
              setSelectedNavItem('projects');
              onShowProjectsList();
            }}
            className={`flex flex-col items-center gap-0.5 px-3 py-1 rounded-lg transition-colors ${
              selectedNavItem === 'projects'
                ? 'text-orange-500'
                : 'text-gray-400'
            }`}
          >
            <GalleryVerticalEnd size={20} />
            <span className="text-[10px] font-medium">Projects</span>
          </button>
          <button
            onClick={() => {
              handleNewConversation();
              setSelectedNavItem('');
            }}
            className="flex items-center justify-center w-10 h-10 -mt-3 bg-orange-500 text-white rounded-full shadow-lg hover:bg-orange-600 transition-colors"
          >
            <Plus size={22} />
          </button>
          <button
            onClick={() => {
              setSelectedNavItem('shared');
              if (currentProjectId) {
                handleBackToGeneralChats();
                selectProject(null);
              }
              onShowSharedList();
            }}
            className={`flex flex-col items-center gap-0.5 px-3 py-1 rounded-lg transition-colors ${
              selectedNavItem === 'shared' ? 'text-orange-500' : 'text-gray-400'
            }`}
          >
            <Share2 size={20} />
            <span className="text-[10px] font-medium">Shared</span>
          </button>
          <button
            onClick={() => {
              setSelectedNavItem('agents');
              if (currentProjectId) selectProject(null);
              handleConversationSelect('');
              onShowAgentList();
            }}
            className={`flex flex-col items-center gap-0.5 px-3 py-1 rounded-lg transition-colors ${
              selectedNavItem === 'agents' ? 'text-orange-500' : 'text-gray-400'
            }`}
          >
            <Grid2x2 size={20} />
            <span className="text-[10px] font-medium">Agents</span>
          </button>
        </nav>
      </div>
    </FilesProvider>
  );
}

export default function App() {
  return (
    <ConversationsProvider>
      <MessageStreamProvider>
        <HashRouterProvider>
          <AppContent />
        </HashRouterProvider>
      </MessageStreamProvider>
      <Toaster />
    </ConversationsProvider>
  );
}
